/**
 * Reactive boolean for dark mode, sourced from the `.dark` class on <html>.
 *
 * Singleton — a single ref and a single MutationObserver are shared across
 * every caller. Previously each call created its own ref/observer, which
 * could diverge (different mount lifecycles meant different instances saw
 * different class-list states). One source of truth eliminates that class
 * of bug and halves observer overhead.
 *
 * Reads the class synchronously on first module access when `document` is
 * available so the initial reactive value matches the pre-hydration script
 * in `nuxt.config.ts`.
 */

let sharedRef: Ref<boolean> | null = null;
let observer: MutationObserver | null = null;

function getSharedRef(): Ref<boolean> {
  if (sharedRef) return sharedRef;

  sharedRef = ref(
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false,
  );

  if (typeof document !== "undefined" && !observer) {
    const sync = () => {
      sharedRef!.value = document.documentElement.classList.contains("dark");
    };
    observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    // Resync on first mount in case the pre-hydration script wrote after our
    // ref was initialized.
    if (typeof window !== "undefined") {
      queueMicrotask(sync);
    }
  }

  return sharedRef;
}

export function useIsDark() {
  return getSharedRef();
}
